import { useEffect, useMemo, useState } from 'react';
import { CheckBox, Spinner } from 'grommet';
import { observer } from 'mobx-react-lite';
import { ApiError } from '/src/api';
import { ClientService } from '/src/api/services/ClientService';
import { ProgramService } from '/src/api/services/ProgramService';
import { Box, Select } from '/src/components';
import { CreditCalculationLogPanel } from '/src/components/admin/CreditCalculationLogPanel';
import { CreditCalculationMissingDataPanel } from '/src/components/admin/CreditCalculationMissingDataPanel';
import { SaveButton } from '/src/components/buttons';
import { ClientsSelect } from '/src/components/clients/ClientsSelect';
import { Card, CardBody, CardHeader, Text } from '/src/components/common';
import { ProgramSelect } from '/src/components/forms/ProgramSelect';
import { useGlobalStore } from '/src/context';
import { useCreditCalculation } from '/src/hooks/useCreditCalculation';
import { Program } from '/src/lib/models/Program';
import { toastMessages } from '/src/lib/toast';
import { ReportingPeriodType, SelectOption } from '/src/lib/types';

const VALIDATION_MESSAGES = [
  'Parsing data...',
  'Validating CIXD Standards...',
  'Validating Equipment Type EERs...',
  'Validating equipment fuel pathways...',
  'Validating Equipment kWh values...',
  'This might take a few minutes...',
];

const VALIDATION_MESSAGE_INTERVAL_MS = 3000;

const PERIOD_VALUE_SEPARATOR = ':';

type SelectedPeriod = { typeId: ReportingPeriodType; startDate: string };

const toPeriodOptions = (options: SelectOption<string>[], typeId: ReportingPeriodType): SelectOption<string>[] =>
  options.map(({ label, value }) => ({ label, value: `${typeId}${PERIOD_VALUE_SEPARATOR}${value}` }));

const parsePeriodValue = (value: string): SelectedPeriod | null => {
  if (value === '') return null;

  const [typeId, startDate] = value.split(PERIOD_VALUE_SEPARATOR);

  return { typeId: Number(typeId), startDate };
};

export const CreditCalculationCard: React.FC = observer(() => {
  /* Context */
  const globalStore = useGlobalStore();

  /* State */
  const [selectedPeriodValue, setSelectedPeriodValue] = useState<string>('');
  const [selectedProgramIds, setSelectedProgramIds] = useState<number[]>([]);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);
  const [skipMissingKwh, setSkipMissingKwh] = useState(false);
  const [programs, setPrograms] = useState<Program[]>([]);
  const [clientOptions, setClientOptions] = useState<SelectOption<number>[]>([]);
  const [validationMessageIndex, setValidationMessageIndex] = useState(0);

  const { status, logLines, updatedCount, failureMessage, missingData, calculate, cancel } = useCreditCalculation();

  /* Computed */
  const selectedPeriod = parsePeriodValue(selectedPeriodValue);
  const selectedProgramTypeId =
    programs.find((program) => program.id === selectedProgramIds[0])?.reporting_period_type_id ?? null;
  const activePeriodTypeId = selectedPeriod?.typeId ?? selectedProgramTypeId;

  const periodOptions = useMemo(
    () => [
      ...(activePeriodTypeId !== ReportingPeriodType.Yearly
        ? toPeriodOptions(globalStore.reportingQuarterOptions, ReportingPeriodType.Quarterly)
        : []),
      ...(activePeriodTypeId !== ReportingPeriodType.Quarterly
        ? toPeriodOptions(globalStore.reportingYearOptions, ReportingPeriodType.Yearly)
        : []),
    ],
    [activePeriodTypeId, globalStore.reportingQuarterOptions, globalStore.reportingYearOptions]
  );

  const programOptions = useMemo(
    () =>
      programs
        .filter((program) => activePeriodTypeId === null || program.reporting_period_type_id === activePeriodTypeId)
        .map((program) => ({ label: program.name, value: program.id })),
    [programs, activePeriodTypeId]
  );

  const hasClientFilter = selectedPeriod !== null && selectedProgramIds.length > 0;
  const isSubmitting = status === 'submitting';
  const isBusy = isSubmitting || status === 'running';
  const canSubmit = selectedPeriod !== null && selectedProgramIds.length > 0 && !isBusy;

  /* Effects */
  useEffect(() => {
    if (!isSubmitting) return;

    setValidationMessageIndex(0);

    const messageInterval = setInterval(() => {
      setValidationMessageIndex((index) => (index + 1) % VALIDATION_MESSAGES.length);
    }, VALIDATION_MESSAGE_INTERVAL_MS);

    return () => clearInterval(messageInterval);
  }, [isSubmitting]);

  useEffect(() => {
    let isStale = false;

    const fetchPrograms = async () => {
      try {
        const programsList = await ProgramService.list();

        if (isStale) return;

        setPrograms(programsList);
      } catch (error) {
        globalStore.handleApiError(error as ApiError, toastMessages.listPrograms.error);
      }
    };

    fetchPrograms();

    return () => {
      isStale = true;
    };
  }, []);

  useEffect(() => {
    if (selectedPeriod === null || selectedProgramIds.length === 0) {
      setClientOptions([]);
      setSelectedClientIds([]);
      return;
    }

    let isStale = false;
    const startReportingQuarter = selectedPeriod.startDate;

    const fetchClients = async () => {
      try {
        const clients = await ClientService.listClientNames({
          program_ids: selectedProgramIds,
          start_reporting_quarter: startReportingQuarter,
        });

        if (isStale) return;

        const options = clients.map((client) => ({ label: client.name, value: client.id }));

        setClientOptions(options);
        setSelectedClientIds((selected) =>
          selected.filter((clientId) => options.some((option) => option.value === clientId))
        );
      } catch (error) {
        if (isStale) return;
        setClientOptions([]);
        setSelectedClientIds([]);
        globalStore.handleApiError(error as ApiError, toastMessages.listClients.error);
      }
    };

    fetchClients();

    return () => {
      isStale = true;
    };
  }, [selectedProgramIds, selectedPeriodValue]);

  /* Methods */
  const handleCalculate = () => {
    if (selectedPeriod === null) return;

    calculate({
      start_reporting_quarter: selectedPeriod.startDate,
      program_ids: selectedProgramIds,
      ...(selectedClientIds.length > 0 ? { client_ids: selectedClientIds } : {}),
      skip_equipment_missing_kwh: skipMissingKwh,
    });
  };

  /* Render */
  return (
    <Box>
      <Card margin={{ vertical: 'medium' }} background="white" elevation="none" round="xsmall">
        <CardHeader
          pad={{ horizontal: 'medium', vertical: 'medium' }}
          border={{ side: 'bottom', color: 'light-4', size: '1px' }}
        >
          <Text size="large" weight="normal">
            Recalculate Credits Generated
          </Text>
        </CardHeader>
        <CardBody pad="medium">
          <Box direction="row" flex="grow" fill="horizontal" gap="1rem">
            <Select
              id="reporting_period"
              label="Reporting Period"
              placeholder="Select Period"
              options={periodOptions}
              value={selectedPeriodValue}
              setValue={setSelectedPeriodValue}
              disabled={isBusy}
              clear
              required
              fill
            />
            <ProgramSelect
              multiple
              label="Programs"
              placeholder="Select Programs"
              options={programOptions}
              selectedProgramIds={selectedProgramIds}
              setSelectedProgramIds={setSelectedProgramIds}
              hideSelectAll
              disabled={isBusy}
              required
              fill
            />
            <Box fill>
              <ClientsSelect
                multiple
                label="Clients"
                placeholder="Select Clients"
                options={clientOptions}
                selectedClientIds={selectedClientIds}
                setSelectedClientIds={setSelectedClientIds}
                disabled={!hasClientFilter || isBusy}
                fill
              />
              <Text size="0.75rem" color="accent-1" margin={{ top: '0.5rem', left: '0.75rem' }}>
                Optional: All clients in the selected programs are included by default.
              </Text>
            </Box>
          </Box>
          <Box direction="row" align="center" gap="medium">
            <CheckBox
              label="Skip equipment missing kWh"
              checked={skipMissingKwh}
              onChange={(event) => setSkipMissingKwh(event.target.checked)}
              disabled={isBusy}
            />
            <Box width="33%">
              <SaveButton label="CALCULATE" onClick={handleCalculate} disabled={!canSubmit} isLoading={isSubmitting} />
            </Box>
            {isSubmitting && (
              <Box direction="row" align="center" gap="xsmall">
                <Spinner size="small" />
                <Box
                  key={validationMessageIndex}
                  animation={[
                    { type: 'slideUp', duration: 1000 },
                    { type: 'fadeIn', duration: 1000 },
                  ]}
                >
                  <Text size="small" color="dark-2">
                    {VALIDATION_MESSAGES[validationMessageIndex]}
                  </Text>
                </Box>
              </Box>
            )}
          </Box>
        </CardBody>
      </Card>

      <CreditCalculationMissingDataPanel missingData={missingData} />
      <CreditCalculationLogPanel
        status={status}
        logLines={logLines}
        updatedCount={updatedCount}
        failureMessage={failureMessage}
        onCancel={cancel}
      />
    </Box>
  );
});
